import { __ } from '@wordpress/i18n';
import { applyFilters } from '@wordpress/hooks';
import { MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import {
	PanelBody,
	TextControl,
	Button,
	Dashicon,
	ToggleControl,
	__experimentalElevation as Elevation,
} from '@wordpress/components';
import { decodeEntities } from '@wordpress/html-entities';
import { Fragment } from '@wordpress/element';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import cloneDeep from 'lodash/cloneDeep';

import placeholderImageUrl from '../../../../includes/assets/img/profile-image.png';
import * as Controls from '../../../controls';
const { IconPicker } = Controls;

const Profile = ({
	attributes,
	setAttributes,
}) => {
	const { style, dataByStyle, linkedinUrl, socialIcon, phoneNumber, profileImage, showQRCode } = attributes;
	const showLinkedin = applyFilters('bwdcv.linkedinUrl', null, {
		linkedinUrl,
		setAttributes,
	});
	const phoneNumberInput = applyFilters('bwdcv.phoneNumberInput', null, {
		phoneNumber,
		setAttributes,
	});
	const socialIconVisibility = applyFilters(
		'bwdcv-social-icon-visibility',
		true
	);
	const afterProfileContact = applyFilters(
		'bwdcv.afterProfileContact',
		null,
		{
			attributes,
			setAttributes,
		}
	);

	const handleSocialDragEnd = (result) => {
		if (!result.destination) return;

		const reorderedSocialIcon = Array.from(socialIcon);
		const [movedItem] = reorderedSocialIcon.splice(result.source.index, 1);
		reorderedSocialIcon.splice(result.destination.index, 0, movedItem);

		setAttributes({ socialIcon: reorderedSocialIcon });
	};
	const socialCloneItems = cloneDeep(socialIcon);
	const addSocialItem = () => {
		const newItems = [
			...socialIcon,
			{
				id: socialIcon.length + 1,
				icon: 'Facebook',
				link: '#',
			},
		];
		setAttributes({ socialIcon: newItems });
	};
	const updateSocialText = (index, attribute, value) => {
		const newItems = [...socialCloneItems];
		newItems[index][attribute] = value;
		setAttributes({ socialIcon: newItems });
	};
	const cloneSocialItem = (index) => {
		const itemToClone = socialIcon[index];
		const newItem = { ...itemToClone };
		setAttributes({ socialIcon: [...socialIcon, newItem] });
	};
	const handleSocialChange = (value, index, attribute) => {
		const newItems = [...socialCloneItems];
		newItems[index][attribute] = value;
		setAttributes({
			socialIcon: newItems,
		});
	};

	const currentData = dataByStyle[style] || {};
	const updateField = (field, value) => {
		setAttributes({
			dataByStyle: {
				...dataByStyle,
				[style]: {
					...currentData,
					[field]: value,
				},
			},
		});
	};


	return (
		<>
			{currentData.profileImage !== undefined && (
				<MediaUploadCheck>
					<MediaUpload
						onSelect={(image) =>
							setAttributes({
								profileImage: {
									url: image.url,
									alt: image.alt,
								},
							})
						}
						allowedTypes={['image']}
						value={profileImage?.id}
						render={({ open }) => (
							<div className="bwdtm-image-control">
								{profileImage ? (
									<div className="bwdtm-image-preview-container">
										<Button
											className="bwdtm-image-preview-wrapper"
											onClick={open}
										>
											<img
												src={profileImage.url}
												alt={profileImage.alt || 'Preview'}
												className="bwdtm-image-preview"
											/>
											<span className="bwdtm-edit-icon">
												<i className="dashicons dashicons-edit"></i>
											</span>
										</Button>
										<Button
											className="bwdtm-close-icon"
											onClick={() =>
												setAttributes({
													profileImage: null,
												})
											}
										>
											<i className="dashicons dashicons-dismiss"></i>
										</Button>
									</div>
								) : (
									<Button
										className="bwdtm-placeholder-image"
										onClick={open}
									>
										<img
											src={placeholderImageUrl}
											alt="Placeholder"
											className="bwdtm-placeholder-image-preview"
										/>
										<span className="bwdcv-profile-image">
											{__(
												'Click to Upload Image',
												'cv-builder'
											)}
										</span>
									</Button>
								)}
							</div>
						)}
					/>
				</MediaUploadCheck>
			)}

			<div className="bwdcvs-personal-info-wrap" id="section1">
				<TextControl
					label={__( 'First Name', 'cv-builder' )}
					value={currentData.firstName}
					onChange={(value) => updateField('firstName', value)}
				/>
				<TextControl
					label={__( 'Last Name', 'cv-builder' )}
					value={currentData.lastName}
					onChange={(value) => updateField('lastName', value)}
				/>
				<TextControl
					label={__( 'Designation', 'cv-builder' )}
					value={decodeEntities(currentData.designation)}
					onChange={(value) => updateField('designation', value)}
				/>
				<ToggleControl
					label={__(
						'Show QR Code',
						'cv-builder'
					)}
					checked={showQRCode}
					onChange={(value) => setAttributes({ showQRCode: value })}
				/>
				{showLinkedin}
				{phoneNumberInput}
			</div>
			{socialIconVisibility && socialIcon.length > 0 && (
				<Fragment>
					<div className="bwdtm-container-wrapper">
						<DragDropContext onDragEnd={handleSocialDragEnd}>
							<Droppable droppableId="social-list">
								{(provided) => (
									<div
										{...provided.droppableProps}
										ref={provided.innerRef}
									>
										{socialCloneItems &&
											socialCloneItems.map(
												(item, index) => (
													<Draggable
														key={index}
														draggableId={`social-${index}`}
														index={index}
													>
														{(
															provided,
															snapshot
														) => (
															<div
																ref={
																	provided.innerRef
																}
																{...provided.draggableProps}
																{...provided.dragHandleProps}
																className={`bwdtm-container ${snapshot.isDragging ? 'dragging' : ''}`}
															>
																<div
																	className="bwdtm-drag-handle"
																	title="Drag Item"
																>
																	☰
																</div>
																<PanelBody
																	title={
																		item.icon
																	}
																	initialOpen={
																		false
																	}
																	className="nested-panel"
																>
																	<IconPicker
																		label={
																			item.icon
																				? `${item.icon}`
																				: __( 'Select Icon', 'cv-builder' )
																		}
																		selectedIcon={
																			item.icon
																		}
																		changeIcon={(
																			value
																		) =>
																			handleSocialChange(
																				value,
																				index,
																				'icon'
																			)
																		}
																	/>
																	<TextControl
																		label={__(
																			'Icon Link',
																			'cv-builder'
																		)}
																		value={
																			item.link
																		}
																		onChange={(
																			value
																		) =>
																			updateSocialText(
																				index,
																				'link',
																				value
																			)
																		}
																		placeholder={__(
																			'Write icon link',
																			'cv-builder'
																		)}
																	/>
																</PanelBody>
																<div className="bwdtm-trash-icon">
																	<Button
																		className="bwdtm-remove-item"
																		icon="trash"
																		title="Delete Item"
																		onClick={() => {
																			setAttributes(
																				{
																					socialIcon:
																						socialIcon.filter(
																							(
																								_,
																								i
																							) =>
																								index !==
																								i
																						),
																				}
																			);
																		}}
																	/>
																	<Button
																		className="bwdtm-clone-item"
																		title="Duplicate Item"
																		onClick={() =>
																			cloneSocialItem(
																				index
																			)
																		}
																	>
																		<svg
																			width="20"
																			height="20"
																			viewBox="0 0 448 512"
																		>
																			<path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z" />
																		</svg>
																	</Button>
																</div>
															</div>
														)}
													</Draggable>
												)
											)}
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
						<Button
							className="bwdtm-add-item"
							onClick={addSocialItem}
						>
							<Dashicon icon="plus" />
							{__('Add Social', 'cv-builder')}
							<Elevation
								active={1}
								focus={5}
								hover={7}
								value={5}
							/>
						</Button>
					</div>
				</Fragment>
			)}
			{afterProfileContact}
		</>
	);
};

export default Profile;
